import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import en from '../locales/en.json';
// uk.json (~89KB) is loaded on demand via ensureLanguage() so it stays out of
// the entry chunk that every (mostly EN) landing visitor downloads. EN is the
// default + fallback, so it must stay bundled synchronously.

export const STORAGE_KEY = 'markflow-lang';
export const SUPPORTED_LANGS = ['en', 'uk'] as const;
export type Lang = (typeof SUPPORTED_LANGS)[number];

/** Default language; everything else is served under a `/<lang>` URL prefix. */
const DEFAULT_LANG: Lang = 'en';

export function langToLocale(lang: string): string {
  const map: Record<string, string> = { en: 'en-US', uk: 'uk-UA' };
  return map[lang] ?? 'en-US';
}

// ── URL ↔ language ──────────────────────────────────────────────────────────
// Public marketing pages are localized via a leading path segment: EN lives at
// the root (`/pocket`), every other language under `/<lang>/…` (`/uk/pocket`).
// The app routes stay unprefixed and keep the in-app toggle.

/** The non-default language a path is prefixed with, or null (= EN root). */
export function langPrefixOf(pathname: string): Lang | null {
  const seg = pathname.split('/')[1];
  return seg === 'uk' ? 'uk' : null;
}

/** Route path with any language prefix removed (`/uk/pocket` → `/pocket`). */
export function stripLangPrefix(pathname: string): string {
  const bare = pathname.replace(/^\/uk(?=\/|$)/, '');
  return bare === '' ? '/' : bare;
}

/** Add the language prefix to a bare route (EN = no prefix). */
export function withLangPrefix(routePath: string, lang: string): string {
  if (lang !== 'uk') return routePath;
  return routePath === '/' ? '/uk' : `/uk${routePath}`;
}

// The public marketing routes that are prerendered per language. On these the
// URL carries the language, so switching language navigates (full reload); off
// them (the app) the switcher changes language in place.
const LOCALIZED_PUBLIC_PATHS = new Set([
  '/',
  '/pocket',
  '/extension',
  '/download',
  '/privacy',
  '/terms',
  '/promise',
]);

/** True when the path (prefix-stripped) is a per-language public route. */
export function isLocalizedPublicPath(pathname: string): boolean {
  return LOCALIZED_PUBLIC_PATHS.has(stripLangPrefix(pathname));
}

// Decide the bootstrap language synchronously, BEFORE React's first render, so a
// hydrated page matches the server DOM exactly:
//   • `/uk/*`            → uk  (prefix is the source of truth; overrides storage)
//   • unprefixed + prerendered HTML already in #root → en  (force, so a stored
//     `uk` preference can't desync the first render from the served EN markup)
//   • otherwise (CSR shell: the app, or an EN prerender capture) → let the
//     detector (localStorage / navigator) choose, preserving the in-app choice.
function bootstrapLng(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  if (langPrefixOf(window.location.pathname)) return 'uk';
  const root = document.getElementById('root');
  if (root && root.hasChildNodes()) return DEFAULT_LANG;
  return undefined;
}

// Track which code-split locales have actually been loaded. i18next's
// `hasResourceBundle('uk', …)` can report true for a configured-but-empty
// language, so we can't rely on it to decide whether to fetch.
const loadedLocales = new Set<string>(['en']);

/**
 * Ensure a language's resource bundle is loaded, dynamically importing the
 * non-default (uk) locale on first use. Awaited before the initial render (see
 * main.tsx) so a `/uk` page paints Ukrainian with no flash of English, and
 * awaited by LanguageSwitcher before an in-app switch.
 */
export async function ensureLanguage(lng: string | undefined): Promise<void> {
  const base = lng?.split('-')[0]; // 'uk-UA' → 'uk', 'en-US' → 'en'
  if (!base || loadedLocales.has(base)) return;
  if (base === 'uk') {
    const uk = (await import('../locales/uk.json')).default;
    i18n.addResourceBundle('uk', 'translation', uk, true, true);
    loadedLocales.add('uk');
    // While uk had no resources, i18next pinned `resolvedLanguage` to the
    // fallback ('en'). publicSeo/activeLang reads resolvedLanguage for <html
    // lang>, canonical and og:locale, so re-resolve now the bundle exists —
    // but only when uk is already the active language (don't switch the app
    // out from under a caller that's pre-loading uk before its own switch).
    if (i18n.language?.split('-')[0] === 'uk') {
      await i18n.changeLanguage(i18n.language);
    }
  }
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
    },
    // When a public route fixes the language from its URL prefix, `lng` forces
    // it and the detector is skipped; on the app's CSR shell it's undefined so
    // the detector runs as before.
    lng: bootstrapLng(),
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGS,
    nonExplicitSupportedLngs: true,
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator'],
      // Don't auto-cache: forcing EN on a prerendered route would otherwise
      // clobber a stored `uk` preference. LanguageSwitcher writes STORAGE_KEY
      // explicitly when the user picks a language.
      caches: [],
      lookupLocalStorage: STORAGE_KEY,
    },
  });

export default i18n;
